import React from 'react';
import { StyleProp, StyleSheet, Text, TextStyle } from 'react-native';
import { Colors, FONT_STACK } from '@shared/styles';
import { UnvalidatedStyles } from '../text/styles';

export type TextAlign = 'left' | 'center' | 'right';

export interface TextProps {
  align?: TextAlign;
  lineThrough?: boolean;
  numberOfLines?: number;
  ellipsizeMode?: 'head' | 'tail' | 'middle';
  children: React.ReactNode | string;
  color?: string;
  allowFontScaling?: boolean;
  flex?: boolean;
  style?: StyleProp<TextStyle>;
  testID?: string;
  onPress?(): void;
  selectable?: boolean;
}

interface PrivateTextProps {
  style?: StyleProp<TextStyle>;
}

function Base({
  numberOfLines,
  ellipsizeMode,
  lineThrough,
  children,
  align,
  color,
  style,
  allowFontScaling,
  flex,
  onPress,
  testID,
  selectable,
}: TextProps & PrivateTextProps) {
  return (
    <Text
      allowFontScaling={allowFontScaling}
      numberOfLines={numberOfLines}
      ellipsizeMode={ellipsizeMode}
      onPress={onPress}
      testID={testID}
      style={[
        MBTypographyStyles.default,
        !!numberOfLines && UnvalidatedStyles.numberOfLines(numberOfLines),
        lineThrough && MBTypographyStyles.lineThrough,
        !!align && { textAlign: align },
        style,
        !!color && { color },
        flex && { flex: 1 },
      ]}
      selectable={selectable}
    >
      {children}
    </Text>
  );
}

function withStyle(style: StyleProp<TextStyle>) {
  // eslint-disable-next-line react/display-name
  return (props: TextProps) => <Base {...props} style={[style, props.style]} />;
}

export const fontTextRegular = {
  fontFamily: FONT_STACK,
  fontStyle: 'normal',
  fontWeight: 'normal',
} as TextStyle;

export const fontDisplayRegular = {
  fontFamily: FONT_STACK,
  fontStyle: 'normal',
  fontWeight: 'normal',
} as TextStyle;

export const fontDisplayBold = {
  fontFamily: FONT_STACK,
  fontWeight: 'bold',
} as TextStyle;

export const fontTextSemibold = {
  fontFamily: FONT_STACK,
  fontWeight: '600',
} as TextStyle;

export const fontTextRegularItalic = {
  fontFamily: FONT_STACK,
  fontStyle: 'italic',
} as TextStyle;

export const MBTypographyStylesRaw = {
  default: {
    color: Colors.v2.purple,
    ...fontTextRegular,
    textRendering: 'geometricPrecision',
  } as TextStyle,
  lineThrough: {
    textDecorationLine: 'line-through',
  } as TextStyle,
  h1: {
    fontSize: 48,
    lineHeight: 53,
    fontFamily: `Maax,${FONT_STACK}`,
    fontWeight: 'bold',
  } as TextStyle,
  h1Mono: {
    fontSize: 48,
    lineHeight: 53,
    letterSpacing: 21,
    fontFamily: 'monospace',
    fontWeight: 'bold',
  } as TextStyle,
  h2: {
    fontSize: 36,
    lineHeight: 43,
    ...fontDisplayRegular,
  } as TextStyle,
  h3: {
    fontSize: 28,
    lineHeight: 33,
    ...fontDisplayRegular,
  } as TextStyle,
  h4: {
    fontSize: 20,
    lineHeight: 30,
    ...fontDisplayBold,
  } as TextStyle,
  h4Semibold: {
    fontSize: 20,
    lineHeight: 30,
    letterSpacing: -0.3,
    ...fontTextSemibold,
  } as TextStyle,
  h4Light: {
    fontSize: 20,
    lineHeight: 30,
    fontWeight: 'normal',
  } as TextStyle,
  subtitle: {
    fontSize: 17,
    lineHeight: 22,
    letterSpacing: -0.3,
    ...fontTextSemibold,
  } as TextStyle,
  body1: {
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: -0.2,
  } as TextStyle,
  body2: {
    fontSize: 14,
    lineHeight: 18,
    letterSpacing: -0.2,
  } as TextStyle,
  overline: {
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: -0.2,
    textTransform: 'uppercase',
  } as TextStyle,
  caption1: {
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: -0.1,
  } as TextStyle,
  caption2: {
    fontSize: 10,
    lineHeight: 13,
    letterSpacing: -0.1,
  } as TextStyle,
};

export const MBTypographyStyles = StyleSheet.create(MBTypographyStylesRaw);

export const MBTextH1 = withStyle(MBTypographyStyles.h1);
export const MBTextH1Mono = withStyle(MBTypographyStyles.h1Mono);
export const MBTextH2 = withStyle(MBTypographyStyles.h2);
export const MBTextH3 = withStyle(MBTypographyStyles.h3);
export const MBTextH4 = withStyle(MBTypographyStyles.h4);
export const MBTextH4Semibold = withStyle(MBTypographyStyles.h4Semibold);
export const MBTextH4Light = withStyle(MBTypographyStyles.h4Light);
export const MBTextSubtitle = withStyle(MBTypographyStyles.subtitle);
export const MBTextBody1 = withStyle(MBTypographyStyles.body1);
export const MBTextBody1Bold = withStyle([MBTypographyStyles.body1, fontTextSemibold]);
export const MBTextBody1Inactive = withStyle([MBTypographyStyles.body1, fontTextRegularItalic]);
export const MBTextBody2 = withStyle(MBTypographyStyles.body2);
export const MBTextBody2Inactive = withStyle([MBTypographyStyles.body2, fontTextRegularItalic]);
export const MBTextBody2Bold = withStyle([MBTypographyStyles.body2, fontTextSemibold]);
export const MBTextOverline = withStyle(MBTypographyStyles.overline);
export const MBTextOverlineBold = withStyle([MBTypographyStyles.overline, fontTextSemibold]);
export const MBTextCaption1 = withStyle(MBTypographyStyles.caption1);
export const MBTextCaption1Bold = withStyle([MBTypographyStyles.caption1, fontTextSemibold]);
export const MBTextCaption2 = withStyle(MBTypographyStyles.caption2);
export const MBTextCaption2Bold = withStyle([MBTypographyStyles.caption2, fontTextSemibold]);
